'use client';

import { useBunja } from 'bunja/react';
import cx from 'classnames';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';

import { useDepositModal } from '@flipster/features/src/shared/wallet-modal/deposit';
import { OpenPosition } from '@flipster/r230522/original/position';
import { NumberInput } from '@flipster/ui';
import { getQuoteCurrencyPrecision } from '@flipster/utils/constants/currency';
import Num from '@flipster/utils/num';
import { PageType } from '@flipster/utils/segment/events/position';

import { adjustMarginStateBunja } from '../state';
import AdjustMarginButtons from './AdjustMarginButtons';
import ErrorMessage from './ErrorMessage';
import PriceInfo from './PriceInfo';
import styles from './inputForm.module.scss';

interface Props {
  currentPosition: OpenPosition;
  page: PageType;
}

const InputForm = ({ currentPosition, page }: Props) => {
  const t = useTranslations();
  const { setAmount, errorAtom, amountAtom, tabAtom, maxAmountAtom, instrumentAtom } = useBunja(adjustMarginStateBunja);

  const tab = useAtomValue(tabAtom);
  const amount = useAtomValue(amountAtom);
  const inputError = useAtomValue(errorAtom);
  const maxAmount = useAtomValue(maxAmountAtom);
  const instrument = useAtomValue(instrumentAtom);

  const openDepositModal = useDepositModal();

  const quoteCurrencyPrecision = getQuoteCurrencyPrecision(instrument.quoteCurrency);

  // add할 잔액이 없는경우
  if (tab === 'add' && Num(maxAmount).isEqualTo(0)) {
    return (
      <section className={cx(styles.desc, styles.noAmount)}>
        <p className={styles.yellow}>
          {t('Position_add_funds.r250828.Add_description_no_asset', { quoteCurrency: instrument.quoteCurrency })}
        </p>
        <p className={styles.yellow}>
          <span>{t('Position_add_funds.r250401.Add_description_instruction_margin')} </span>
          <button type='button' className={styles.addAsset} onClick={() => openDepositModal()}>
            {t('Position_add_funds.r250401.Add_description_add_assets_postfix')}
          </button>
        </p>
      </section>
    );
  }

  // remove할 잔액이 없는경우
  if (tab === 'remove' && Num(maxAmount).isEqualTo(0)) {
    return (
      <section className={cx(styles.desc, styles.noAmount)}>
        <p className={styles.yellow}>{t('Position_add_funds.r250401.Remove_disabled_description_margin')}</p>
      </section>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.form}>
        <NumberInput
          value={amount}
          onChange={setAmount}
          shape='inner'
          precision={quoteCurrencyPrecision}
          placeholder={t('Position_add_funds.r250827.Enter_quote_currency_amount', {
            quoteCurrency: instrument.quoteCurrency,
          })}
          size='large'
          className={styles.controlNumberInput}
        />
        {inputError && <ErrorMessage message={inputError.message} action={inputError.action} />}
      </div>
      <PriceInfo tab={tab} currentPosition={currentPosition} instrument={instrument} />
      <AdjustMarginButtons maxAmount={maxAmount} currentPosition={currentPosition} page={page} />
    </div>
  );
};

export default InputForm;
